/* istanbul ignore file */

import {
  createEventCallbackRedirect,
  EVENT_TIMEOUT_IN_MS,
  sendGAEvent,
} from '@noths/polaris-client-google-analytics';
import { EventsMap } from '@noths/polaris-client-next-redux';
import { PlacementEntry } from '@noths/polaris-client-ribbons-design-system';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getPersonalisationBreakdown } from 'src/redux/personalisation/helpers/selectorHelpers';
import { SimilarProduct } from 'src/types';
import { generateProductsForEvent } from 'src/utils/googleAnalytics';
import {
  recommendedProductsShelfProductClick,
  recommendedProductsShelfVisible,
} from '../modules/actions';
import {
  ProductClickEvent,
  ProductImpressionsInitialEvent,
  ProductShelfImpressionEvent,
} from './events';

export const GAEventsMap: EventsMap<ReduxApplicationState> = {
  [recommendedProductsShelfVisible.type]: (prevState, _nextState, action) => {
    const {
      payload: { placement: placementName },
    } = action as ReturnType<typeof recommendedProductsShelfVisible>;
    const { placementStrategy = '', products } = prevState.richRelevance[placementName];

    sendGAEvent<ProductImpressionsInitialEvent>({
      ecommerce: null,
    });

    const eventLabel = `${placementStrategy || ''} : ${placementName}`;

    const {
      amountAdded: addedPersonalisationCost,
      amountRemoved: removedPersonalisationCost,
    } = getPersonalisationBreakdown(prevState.personalisation.selectedPersonalisations, 'GBP');

    sendGAEvent<ProductShelfImpressionEvent>({
      event: 'product_impressions',
      event_category: 'Rich Relevance',
      event_action: 'Product Impressions',
      event_label: eventLabel,
      ecommerce: {
        impressions: generateProductsForEvent({
          products: products!,
          placementName,
          placementStrategy,
          personalisationsTotalAmount: addedPersonalisationCost - removedPersonalisationCost,
        }),
      },
    });
  },
  [recommendedProductsShelfProductClick.type]: (prevState, _nextState, action) => {
    const {
      payload: { placement: placementName, productUrl, productCode },
    } = action as ReturnType<typeof recommendedProductsShelfProductClick>;
    const {
      placementStrategy = '',
      products,
    }: PlacementEntry<SimilarProduct> = prevState.richRelevance[placementName];

    if (products === null) {
      /*
        This would result from an API request failing, however this function shouldn't be callable in that case.
        This guard is in place for type safety.
      */
      return;
    }

    const productIndex = products.findIndex(({ code }) => productCode === code);
    const positionInList = productIndex + 1;
    const product = products[productIndex];

    sendGAEvent<ProductImpressionsInitialEvent>({
      ecommerce: null,
    });

    const eventLabel = `${positionInList} : ${placementStrategy || ''} : ${placementName}`;

    const {
      amountAdded: addedPersonalisationCost,
      amountRemoved: removedPersonalisationCost,
    } = getPersonalisationBreakdown(prevState.personalisation.selectedPersonalisations, 'GBP');

    sendGAEvent<ProductClickEvent>({
      event: 'product_clicks',
      event_category: 'Rich Relevance',
      event_action: 'Product Click',
      event_label: eventLabel,
      algonomy_tracking_URL: product.linkURL,
      ecommerce: {
        click: {
          actionField: {
            list: `product - rich relevance - ${placementName} - ${placementStrategy}`,
          },
          products: generateProductsForEvent({
            products: [product!],
            placementName,
            placementStrategy,
            positionInList,
            personalisationsTotalAmount: addedPersonalisationCost - removedPersonalisationCost,
          }),
        },
      },
      eventCallback: createEventCallbackRedirect(productUrl),
      eventTimeout: EVENT_TIMEOUT_IN_MS,
    });
  },
};
